import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'

import ChipSelect from 'components/ChipSelect'
import { LANGUAGE_FROM_TOLGEE } from 'consts/languageFromTolgee'
import type { GetTopicsFilterQuery, GetTopicsFilterQueryResult } from 'generated/graphql'
import { useCurrentLanguage, useTranslate } from 'lib/tolgee'
import { UserStore } from 'stores'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import { GET_TOPIC_BY_ID, GET_TOPICS_FILTER } from './query'

type availableLanguages = 'por' | 'spa' | 'eng' | 'all'

interface ITopicsFilter {
  languages?: availableLanguages[]
  categories?: string
}

const DEFAULT_VARIABLES = {
  pagination: {
    take: 30,
  },
  filters: {
    types: ['topic'],
    timeRange: {
      timeUnit: 'month',
      timeValue: 12,
    },
  },
  sort: {
    field: 'avgEngagement',
    order: 'desc',
  },
}

export default function TopicsFilter({ languages, categories }: ITopicsFilter) {
  const [defaultOptions, setDefaultOptions] = useState([])

  const t = useTranslate()
  const currentLanguage = useCurrentLanguage()
  const location = useLocation()
  const history = useHistory()

  const [getTopicsFilter, { refetch, loading }] = useLazyQuery<GetTopicsFilterQuery>(GET_TOPICS_FILTER)

  const currentTopicId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)

    const topicsValue = searchParams.get('topics')

    return topicsValue
  }, [location.search])

  const { data, loading: loadingFilter } = useQuery(GET_TOPIC_BY_ID, {
    variables: {
      territoryId: currentTopicId,
    },
    skip: !currentTopicId,
  })

  const queryVariables = useMemo(() => {
    return {
      ...DEFAULT_VARIABLES,
      filters: {
        ...DEFAULT_VARIABLES?.filters,
        language: languages,
        category: categories ? [categories] : [],
      },
    }
  }, [categories, languages])

  const formatData = useCallback(
    (dataFromQuery: GetTopicsFilterQueryResult['data']) => {
      const queryData = dataFromQuery?.listTerritoriesPaginated?.data ?? []

      if (!queryData.length) {
        return []
      }

      const formattedData = queryData.reduce(
        (prev, curr) => {
          if (curr?.__typename !== 'Topic') {
            return prev
          }
          const language = curr?.language
          return {
            ...prev,
            [language]: {
              ...prev[language],
              options: [
                ...(prev[language]?.options || []),
                {
                  label: curr?.name,
                  value: curr?.territoryId,
                },
              ],
            },
          }
        },
        {
          por: {
            label: t(TOLGEE_GLOBALS.por),
            options: [],
          },
          spa: {
            label: t(TOLGEE_GLOBALS.spa),
            options: [],
          },
          eng: {
            label: t(TOLGEE_GLOBALS.eng),
            options: [],
          },
          all: {
            label: t(TOLGEE_GLOBALS.musicTopics),
            options: [],
          },
        }
      )

      const prefferedLanguage = LANGUAGE_FROM_TOLGEE[currentLanguage]
      const availableKeys = Object.keys(formattedData)
      const prefferedLanguageIndex = availableKeys.indexOf(prefferedLanguage)

      const isNotFound = -1
      const isFirstInArray = 0

      if (![isNotFound, isFirstInArray].includes(prefferedLanguageIndex)) {
        availableKeys[prefferedLanguageIndex] = availableKeys[0]
        availableKeys[0] = prefferedLanguage
      }

      const orderedOptions = availableKeys.map(item => formattedData[item])

      return [{ label: 'All topics', value: null }, ...orderedOptions]
    },
    [currentLanguage, t]
  )

  const searchTerritories = (search, callback) => {
    try {
      refetch({
        pagination: {
          take: 20,
        },
        ...queryVariables,
        filters: {
          ...queryVariables?.filters,
          title: search,
        },
      })
        .then(data => {
          const formattedData = formatData(data?.data)
          return formattedData
        })
        .then(response => {
          callback(response)
        })
    } catch (error) {
      console.error('Error @GET_TOPICS_FILTER >>>>', error)
    }
  }

  const handleChange = value => {
    const newSearchParams = new URLSearchParams(location.search)
    if (value?.value) {
      newSearchParams.set('topics', String(value?.value))
    } else {
      newSearchParams.delete('topics')
    }

    analyticsTracker.eventTrack(analyticsTracker.eventNames.TOP.FILTER_TOPIC, {
      selectedTopic: value?.label,
    })

    const filterSearchParams = newSearchParams.toString()
    history.push({ search: filterSearchParams })
  }

  useEffect(() => {
    try {
      const isAllLanguages = languages?.length > 1
      const prefferedLanguage = LANGUAGE_FROM_TOLGEE[currentLanguage]

      const userLanguages = UserStore.userLanguageOptions
        .filter(language => !language.disabled)
        .map(language => {
          return language.value
        })
      const isPrefferedLanguageAvailable = userLanguages.includes(prefferedLanguage)

      const defaultLanguage = isPrefferedLanguageAvailable ? [prefferedLanguage] : [userLanguages[0]]

      getTopicsFilter({
        variables: {
          ...queryVariables,
          filters: {
            ...queryVariables.filters,
            ...(isAllLanguages && { language: defaultLanguage }),
          },
          pagination: {
            take: 400,
          },
        },
      }).then(data => {
        const formattedData = formatData(data?.data)
        setDefaultOptions(formattedData)
      })
    } catch (error) {
      console.error('Error @GET_TOPICS_FILTER >>>>', error)
    }
  }, [currentLanguage, formatData, getTopicsFilter, languages?.length, queryVariables])

  const currentValue = useMemo(() => {
    return {
      value: currentTopicId,
      label: data?.getTerritory?.name ?? '',
    }
  }, [data?.getTerritory?.name, currentTopicId])

  return (
    <ChipSelect
      onSelect={handleChange}
      selectedValue={currentValue}
      options={defaultOptions}
      isDisabled={loading || loadingFilter}
      label="All topics"
      onType={searchTerritories}
      isAsync
    />
  )
}
